/**
 * ReceiptPayment.jsx
 * Proper accounting format: head-wise totals, one line per head.
 * Matches standard R&P format as per Trial Balance accounting.
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, CheckCircle, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { getReceiptPaymentReport } from '../../api/yea.api';
import { downloadRPPDF, downloadRPExcel } from '../../utils/exportUtils';
import Button from '../../components/common/Button';
import '../accounting/yea.css';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const ReceiptPayment = ({ societyId, fyId, fyName, societyName }) => {
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getReceiptPaymentReport(societyId, fyId);
      setReport(r.data.report);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [societyId, fyId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!report)  return null;

  const { receipts, payments, receiptSideTotal } = report;
  const openingBanks = receipts.opening.banks || [];
  const closingBanks = payments.closingBanks  || [];
  const receiptLines = receipts.lines  || [];
  const paymentLines = payments.lines  || [];
  const maxRows      = Math.max(receiptLines.length, paymentLines.length);

  return (
    <div className="animate-in">
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1rem', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <h2>Receipt &amp; Payment Account</h2>
          <p className="text-sm text-muted" style={{ marginTop:'0.2rem' }}>
            {societyName} &nbsp;|&nbsp; FY: {fyName || report.fy}
          </p>
        </div>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          <Button variant="ghost" size="sm" icon={FileText}        onClick={() => downloadRPPDF(report, fyName, societyName)}>PDF</Button>
          <Button variant="ghost" size="sm" icon={FileSpreadsheet} onClick={() => downloadRPExcel(report, fyName, societyName)}>Excel</Button>
          <Button variant="ghost" size="sm" icon={RefreshCw}       onClick={fetchReport}>Refresh</Button>
        </div>
      </div>

      {/* Tally bar */}
      <div className="rp-tally-bar ok" style={{ marginBottom:'1.25rem' }}>
        <CheckCircle size={15} /> Accounts are balanced and tallying
      </div>

      {/* Society name header */}
      <div style={{ textAlign:'center', marginBottom:'1rem' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:700, color:'var(--text)', textTransform:'uppercase' }}>
          {societyName}
        </div>
        <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>
          Receipt and Payment Account for the period {fyName || report.fy}
        </div>
      </div>

      {/* 4-column accounting table */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table className="ledger-table" style={{ width:'100%' }}>
          <thead>
            <tr>
              <th style={{ background:'var(--success)', color:'#fff', width:'35%', textAlign:'left' }}>Particulars (Receipts Dr)</th>
              <th style={{ background:'var(--success)', color:'#fff', width:'15%', textAlign:'right' }}>Amount (₹)</th>
              <th style={{ background:'var(--danger)',  color:'#fff', width:'35%', textAlign:'left' }}>Particulars (Payments Cr)</th>
              <th style={{ background:'var(--danger)',  color:'#fff', width:'15%', textAlign:'right' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {/* Opening balance rows */}
            <tr style={{ background:'var(--bg-raised)' }}>
              <td style={{ fontWeight:600 }}>Opening Balance</td>
              <td className="amount-cell" />
              <td />
              <td />
            </tr>
            <tr>
              <td style={{ paddingLeft:'1.5rem', color:'var(--text-secondary)' }}>Cash Balance</td>
              <td className="amount-cell">{fmt(receipts.opening.cash)}</td>
              <td />
              <td />
            </tr>
            {openingBanks.map((b, i) => (
              <tr key={i}>
                <td style={{ paddingLeft:'1.5rem', color:'var(--text-secondary)' }}>Bank — {b.bankName}</td>
                <td className="amount-cell">{fmt(b.amount)}</td>
                <td />
                <td />
              </tr>
            ))}

            <tr><td colSpan={4} style={{ borderTop:'1px solid var(--border-subtle)', padding:'0.2rem' }} /></tr>

            {/* Head-wise income vs expense — zipped */}
            {Array.from({ length: maxRows }, (_, i) => {
              const r = receiptLines[i];
              const p = paymentLines[i];
              return (
                <tr key={i}>
                  <td style={{ color:'var(--text)', fontSize:'0.875rem' }}>{r?.headName || ''}</td>
                  <td className="amount-cell" style={{ color: r ? 'var(--success)' : undefined, fontWeight: r ? 500 : 400 }}>
                    {r ? fmt(r.amount) : ''}
                  </td>
                  <td style={{ color:'var(--text)', fontSize:'0.875rem' }}>{p?.headName || ''}</td>
                  <td className="amount-cell" style={{ color: p ? 'var(--danger)' : undefined, fontWeight: p ? 500 : 400 }}>
                    {p ? fmt(p.amount) : ''}
                  </td>
                </tr>
              );
            })}

            <tr><td colSpan={4} style={{ borderTop:'1px solid var(--border-subtle)', padding:'0.2rem' }} /></tr>

            {/* Closing balances */}
            <tr style={{ background:'var(--bg-raised)' }}>
              <td />
              <td />
              <td style={{ fontWeight:600 }}>Closing Balance</td>
              <td className="amount-cell" />
            </tr>
            <tr>
              <td />
              <td />
              <td style={{ paddingLeft:'1.5rem', color:'var(--text-secondary)' }}>Cash Balance</td>
              <td className="amount-cell" style={{ fontWeight:600, color: payments.closingCash < 0 ? 'var(--danger)' : 'var(--gold)' }}>
                {fmt(payments.closingCash)}
              </td>
            </tr>
            {closingBanks.map((b, i) => (
              <tr key={i}>
                <td />
                <td />
                <td style={{ paddingLeft:'1.5rem', color:'var(--text-secondary)' }}>Bank — {b.bankName}</td>
                <td className="amount-cell" style={{ color: b.balance < 0 ? 'var(--danger)' : 'var(--text)' }}>
                  {fmt(b.balance)}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr style={{ background:'var(--bg-overlay)', fontWeight:700, fontSize:'0.9rem' }}>
              <td style={{ padding:'0.85rem 1rem', color:'var(--text)' }}>Total</td>
              <td className="amount-cell" style={{ color:'var(--gold)', fontSize:'1rem' }}>{fmt(receiptSideTotal)}</td>
              <td style={{ padding:'0.85rem 1rem', color:'var(--text)' }}>Total</td>
              <td className="amount-cell" style={{ color:'var(--gold)', fontSize:'1rem' }}>{fmt(receiptSideTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ReceiptPayment;
